import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PuzzlePieceIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';

interface Integration {
  id: string;
  type: string;
  name: string;
  description: string;
  icon: string;
  status: 'connected' | 'disconnected' | 'error';
  config?: any;
  lastSync?: Date;
  comingSoon?: boolean;
}

const IntegrationsPage: React.FC = () => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/integrations');
      setIntegrations(response.data);
    } catch (error) {
      console.error('Error fetching integrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIntegrationIcon = (type: string) => {
    const icons: Record<string, string> = {
      DISCORD: '🎮',
      YOUTUBE: '📺',
      TWITCH: '🎥',
      TWITTER: '🐦',
      FACEBOOK: '👥',
      INSTAGRAM: '📷',
      PAYPAL: '💳',
      STRIPE: '💰',
      ZAPIER: '⚡',
      WEBHOOK: '🔗',
      SLACK: '💬',
    };
    return icons[type] || '🔌';
  };

  const availableIntegrations = [
    {
      type: 'DISCORD',
      name: 'Discord',
      description: 'Sync roles and send notifications to your Discord server',
      link: '/dashboard/integrations/discord',
    },
    {
      type: 'YOUTUBE',
      name: 'YouTube',
      description: 'Import videos and sync subscriber count',
      link: '/dashboard/integrations/youtube',
    },
    {
      type: 'TWITCH',
      name: 'Twitch',
      description: 'Monitor live streams and import VODs',
      link: '/dashboard/integrations/twitch',
    },
    {
      type: 'TWITTER',
      name: 'Twitter/X',
      description: 'Auto-post updates to your Twitter feed',
      link: '/dashboard/integrations/twitter',
      comingSoon: true,
    },
    {
      type: 'FACEBOOK',
      name: 'Facebook',
      description: 'Share content to your Facebook page',
      link: '/dashboard/integrations/facebook',
      comingSoon: true,
    },
    {
      type: 'INSTAGRAM',
      name: 'Instagram',
      description: 'Post images to Instagram Business account',
      link: '/dashboard/integrations/instagram',
      comingSoon: true,
    },
    {
      type: 'PAYPAL',
      name: 'PayPal',
      description: 'Accept payments and process payouts',
      link: '/dashboard/integrations/paypal',
      comingSoon: true,
    },
    {
      type: 'ZAPIER',
      name: 'Zapier',
      description: 'Connect to 5000+ apps via Zapier',
      link: '/dashboard/integrations/zapier',
      comingSoon: true,
    },
    {
      type: 'WEBHOOK',
      name: 'Webhooks',
      description: 'Send events to custom webhook endpoints',
      link: '/dashboard/integrations/webhooks',
    },
    {
      type: 'SLACK',
      name: 'Slack',
      description: 'Send notifications to Slack channels',
      link: '/dashboard/integrations/slack',
    },
  ];

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <PuzzlePieceIcon className="h-8 w-8 text-blue-600" />
          Integrations
        </h1>
        <p className="mt-2 text-gray-600">
          Connect your favorite platforms and tools to enhance your creator experience
        </p>
      </div>

      {/* Connected Integrations */}
      {integrations.filter(i => i.status === 'connected').length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Connected</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {integrations
              .filter(i => i.status === 'connected')
              .map((integration) => (
                <Link
                  key={integration.id}
                  to={`/dashboard/integrations/${integration.type.toLowerCase()}`}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{getIntegrationIcon(integration.type)}</span>
                      <div>
                        <h3 className="font-semibold text-gray-900">{integration.name}</h3>
                        <p className="text-sm text-gray-500 mt-1">{integration.description}</p>
                      </div>
                    </div>
                    <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
                  </div>
                  {integration.lastSync && (
                    <p className="text-xs text-gray-400 mt-4">
                      Last synced: {new Date(integration.lastSync).toLocaleString()}
                    </p>
                  )}
                </Link>
              ))}
          </div>
        </div>
      )}

      {/* Available Integrations */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Available Integrations</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {availableIntegrations.map((integration) => {
            const isConnected = integrations.some(
              i => i.type === integration.type && i.status === 'connected'
            );

            return (
              <div
                key={integration.type}
                className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${
                  integration.comingSoon ? 'opacity-60' : 'hover:shadow-md'
                } transition-shadow`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{getIntegrationIcon(integration.type)}</span>
                    <div>
                      <h3 className="font-semibold text-gray-900">{integration.name}</h3>
                      {integration.comingSoon && (
                        <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
                          Coming Soon
                        </span>
                      )}
                    </div>
                  </div>
                  {isConnected && (
                    <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
                  )}
                </div>
                <p className="text-sm text-gray-500 mb-4">{integration.description}</p>
                {!integration.comingSoon && (
                  <Link
                    to={integration.link}
                    className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    {isConnected ? 'Manage' : 'Connect'}
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Error State */}
      {integrations.filter(i => i.status === 'error').length > 0 && (
        <div className="mt-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Needs Attention</h2>
          <div className="space-y-3">
            {integrations
              .filter(i => i.status === 'error')
              .map((integration) => (
                <div
                  key={integration.id}
                  className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                    <div>
                      <h3 className="font-semibold text-red-900">{integration.name}</h3>
                      <p className="text-sm text-red-700">
                        Connection error - please reconnect
                      </p>
                    </div>
                  </div>
                  <Link
                    to={`/dashboard/integrations/${integration.type.toLowerCase()}`}
                    className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700"
                  >
                    Reconnect
                  </Link>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationsPage;
